import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';

interface AgentFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    form: any;
    schema: any;

    meta: any;
}

const state: AgentFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    form: null,
    schema: null,

    meta: {}
};

const initState = JSON.parse(JSON.stringify(state));

export const AGENT_DATA = 'GET_AGENT_DATA';
export const AGENT_SCHEMA = 'GET_AGENT_SCHEMA';

export const actions: ActionTree<AgentFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit, getters, dispatch }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(AGENT_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),

            ]).then((result: ServerResult) => {
                commit('SET_FORM_DATA', {
                    document: result.getOperation(AGENT_DATA).getContent(),
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(AGENT_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:agents`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    schema: result.getOperation(AGENT_SCHEMA).getContent(),
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

    saveForm({ commit, getters, dispatch }, params) {
        const vm = (this as any)._vm;

        const normalizedForm = state.schema.fields.reduce((prev: any, field: any) => {
            const fieldName = field.code;

            if (
                (state.form[fieldName] !== null && state.form[fieldName] !== undefined) ||
                ['preferred_product_container_id', 'preferred_product_container_2_id',
                'preferred_product_container_3_id', 'secondary_manager_id'].includes(fieldName)
            ) {
                switch(field.type) {
                    case 'date': {
                        prev[fieldName] = moment(state.form[fieldName]).utc().local().format('YYYY-MM-DD');
                        break;
                    }
                    default: {
                        prev[fieldName] = state.form[fieldName]
                    }
                }

            }

            return prev
        }, {});

        return new Promise((resolve, reject) => {
            commit('START_LOADING');
            let saveQuery = null;
            if (params.id) {
                saveQuery = vm.$q('AGENT_DATA_SAVE')
                    .update(params.id, normalizedForm)
            } else {
                saveQuery = vm.$q('AGENT_DATA_SAVE')
                    .create('s:agents', normalizedForm)
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('AGENT_DATA_SAVE');
                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', { document: operationResult.getContent() })

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('[Agents.AgentForm.saveForm] ERROR', { params, error });
                commit('LOADED_ERROR');
                reject(error);
            });
        })
    },
};

export const mutations: MutationTree<AgentFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = initState[key]
        }
    },
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_FORM_DATA(state, {document}) {
        state.document = document.document;
        state.schema = document.schema;
        state.meta = fetchEnumsFromSchema(document.schema);

        state.form = JSON.parse(JSON.stringify(state.document));
    },

    GENERATE_FORM_DATA(state, {schema}) {
        state.document = {};
        state.schema = schema.schema;
        state.meta = fetchEnumsFromSchema(schema.schema);

        const formData = state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = null;
            return prev;
        }, {})

        state.form = formData;
    },

    ON_FORM_SAVE(state, {document}) {
        state.document = document.document;
        state.schema = document.schema;

        state.form = JSON.parse(JSON.stringify(state.document));
    },

    CHANGE_VALUE(state: AgentFormState, {field, newValue}) {
        switch (field) {
            case 'lk_allow_calc_containers': {
                if (!newValue) {
                    state.form['lk_allow_calc_weight'] = true
                    state.form['lk_selected_calc_method'] = 'weight'
                }
                state.form[field] = newValue;
                break
            }
            case 'lk_allow_calc_weight': {
                if (!newValue) {
                    state.form['lk_allow_calc_containers'] = true
                    state.form['lk_selected_calc_method'] = 'containers'
                }
                state.form[field] = newValue;
                break
            }
            case 'credit': {
                state.form[field] = parseInt(newValue);
                break
            }
            default:
                state.form[field] = newValue;
        }
    },
};

const getters = {
    fields: (state: AgentFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_agent_details_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
